/**
 * Message content guards — detect and suppress malformed assistant messages.
 *
 * When the OpenAI model fails to invoke a tool (e.g., schema validation error),
 * it sometimes falls back to emitting the raw tool arguments as a text response.
 * These guards prevent that leaked JSON from reaching the UI.
 */

/**
 * Returns true if text looks like a raw JSON tool-argument object that
 * leaked into assistant text output.
 *
 * Uses a structural heuristic: small JSON objects with only primitive
 * values are almost certainly tool arguments, not intentional assistant
 * responses. The assistant never sends a bare JSON object as its entire
 * message — real responses always contain prose.
 *
 * No per-tool key lists needed; new tools don't require guard updates.
 */
export function looksLikeLeakedToolArgs(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return false;
  try {
    const obj = JSON.parse(trimmed);
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj))
      return false;
    const keys = Object.keys(obj);
    // Tool args are small objects (1–10 keys) with simple values.
    if (keys.length === 0 || keys.length > 10) return false;
    // Every value must be a primitive or short array — no nested prose
    const isLeaked = keys.every((k) => {
      const v = obj[k];
      return (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean' ||
        v === null ||
        (Array.isArray(v) && v.length <= 5)
      );
    });
    if (isLeaked) {
      console.debug(
        '[message-guard] Suppressed leaked tool args:',
        trimmed.slice(0, 120)
      );
    }
    return isLeaked;
  } catch {
    return false;
  }
}
